import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { formatApiError } from "@/lib/api";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";

export default function Login() {
    const { login, register, loginWithGoogle } = useAuth();
    const { t } = useLang();
    const navigate = useNavigate();

    useEffect(() => {
        const meta = document.createElement("meta");
        meta.name = "robots";
        meta.content = "noindex, nofollow";
        document.head.appendChild(meta);
        return () => { document.head.removeChild(meta); };
    }, []);

    const [mode, setMode] = useState("signin"); // signin | signup
    const [form, setForm] = useState({ name: "", username: "", email: "", password: "" });
    const [loading, setLoading] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const data = mode === "signin"
                ? await login(form.email, form.password)
                : await register(form);
            toast.success(mode === "signin" ? t.auth.successIn : t.auth.successUp);
            navigate(data.role === "admin" ? "/admin" : "/");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong.");
        } finally {
            setLoading(false);
        }
    };

    const inputCls = "w-full bg-background border border-border rounded-md px-4 py-3 text-obsidian placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-cyan focus:border-transparent transition-shadow";

    return (
        <div className="bg-background min-h-screen flex flex-col">
            <Header />
            <section className="flex-1 grid place-items-center px-6 pt-32 pb-24">
                <div className="w-full max-w-md">
                    <div className="flex bg-secondary rounded-full p-1 mb-8" data-testid="auth-mode-toggle">
                        {[["signin", t.auth.signIn], ["signup", t.auth.signUp]].map(([m, label]) => (
                            <button
                                key={m}
                                data-testid={`mode-${m}`}
                                onClick={() => setMode(m)}
                                className={`flex-1 eyebrow rounded-full py-2.5 transition-colors duration-300 ${mode === m ? "bg-obsidian text-white" : "text-obsidian/60 hover:text-obsidian"}`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    <h1 className="font-heading font-black text-4xl tracking-tighter text-obsidian leading-none">
                        {mode === "signin" ? t.auth.signInTitle : t.auth.signUpTitle}
                    </h1>
                    <p className="mt-3 text-muted-foreground">{mode === "signin" ? t.auth.signInSub : t.auth.signUpSub}</p>

                    <button
                        type="button"
                        data-testid="google-signin-button"
                        onClick={loginWithGoogle}
                        className="mt-8 w-full inline-flex items-center justify-center gap-3 rounded-full border border-border bg-card px-8 py-4 text-obsidian font-semibold hover:-translate-y-0.5 hover:border-obsidian transition-all duration-300"
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                            <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5a5.6 5.6 0 0 1-2.4 3.7v3h3.9c2.3-2.1 3.5-5.2 3.5-8.9z"/>
                            <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.9-3c-1.1.7-2.4 1.1-4 1.1-3.1 0-5.7-2.1-6.6-4.9H1.4v3.1A12 12 0 0 0 12 24z"/>
                            <path fill="#FBBC05" d="M5.4 14.3a7.2 7.2 0 0 1 0-4.6V6.6H1.4a12 12 0 0 0 0 10.8l4-3.1z"/>
                            <path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4A12 12 0 0 0 12 0 12 12 0 0 0 1.4 6.6l4 3.1C6.3 6.9 8.9 4.8 12 4.8z"/>
                        </svg>
                        {t.auth.google}
                    </button>

                    <div className="flex items-center gap-4 my-6">
                        <span className="flex-1 h-px bg-border" />
                        <span className="eyebrow text-muted-foreground">{t.auth.or}</span>
                        <span className="flex-1 h-px bg-border" />
                    </div>

                    <form onSubmit={submit} data-testid="auth-form" className="space-y-4">
                        {mode === "signup" && (
                            <>
                                <div>
                                    <label className="eyebrow text-muted-foreground block mb-2">{t.auth.name}</label>
                                    <input data-testid="auth-name" className={inputCls} value={form.name} onChange={set("name")} placeholder={t.auth.phName} required />
                                </div>
                                <div>
                                    <label className="eyebrow text-muted-foreground block mb-2">{t.auth.username}</label>
                                    <input data-testid="auth-username" className={inputCls} value={form.username} onChange={set("username")} placeholder={t.auth.phUsername} required />
                                </div>
                            </>
                        )}
                        <div>
                            <label className="eyebrow text-muted-foreground block mb-2">{t.auth.email}</label>
                            <input data-testid="auth-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder={t.auth.phEmail} required />
                        </div>
                        <div>
                            <label className="eyebrow text-muted-foreground block mb-2">{t.auth.password}</label>
                            <input data-testid="auth-password" type="password" className={inputCls} value={form.password} onChange={set("password")} placeholder="••••••••" required minLength={6} />
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="auth-submit-button"
                            className="group eyebrow w-full inline-flex items-center justify-center gap-2 rounded-full bg-obsidian text-white px-8 py-4 hover:-translate-y-0.5 hover:bg-cyan hover:text-obsidian transition-all duration-300 disabled:opacity-50"
                        >
                            {loading ? (mode === "signin" ? t.auth.signingIn : t.auth.creating) : (mode === "signin" ? t.auth.signIn : t.auth.signUp)}
                            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                        </button>
                    </form>

                    <button
                        data-testid="auth-toggle-link"
                        onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
                        className="mt-6 text-sm text-muted-foreground hover:text-cyan transition-colors"
                    >
                        {mode === "signin" ? t.auth.toggleToSignUp : t.auth.toggleToSignIn}
                    </button>
                </div>
            </section>
            <Footer />
        </div>
    );
}
